// 報表中心: 客戶關係 / 供應商 / 項目利潤
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, BarChart as RBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList } from 'recharts';
import { api, money, fmtDate, r2 } from '../api.js';
import { Loading, Empty, PageHead } from '../ui.jsx';
import { ChartTip, CHART_COLORS } from '../charts.jsx';

const years = (() => { const y = new Date().getFullYear(); return [y + 1, y, y - 1, y - 2, y - 3].filter(x => x <= y + 1); })();

function BarChart({ data, color, moneyLabels = true }) {
  const max = Math.max(...data.map(d => d.v), 1);
  return (
    <div style={{ height: 260, marginTop: 4 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RBarChart data={data} margin={{ top: 18, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,.12)" vertical={false} />
          <XAxis dataKey="l" tick={{ fontSize: 11, fill: '#b5b5b5' }} axisLine={false} tickLine={false} interval={0} />
          <YAxis tick={{ fontSize: 10.5, fill: '#b5b5b5' }} axisLine={false} tickLine={false}
            tickFormatter={v => v >= 1000 ? Math.round(v / 1000) + 'K' : v} domain={[0, max * 1.15]} />
          <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(255,255,255,.06)' }} />
          <Bar dataKey="v" name="金額" radius={[9, 9, 3, 3]} barSize={38} maxBarSize={52}>
            {data.map((d, i) => <Cell key={i} fill={d.v < 0 ? 'var(--coral)' : (color || CHART_COLORS[i % CHART_COLORS.length])} />)}
            {moneyLabels ? <LabelList dataKey="v" position="top" formatter={v => Math.abs(v) >= 1000 ? Math.round(v / 1000) + 'K' : v}
              style={{ fontSize: 10.5, fill: '#b5b5b5', fontWeight: 700 }} /> : null}
          </Bar>
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Reports() {
  const nav = useNavigate();
  const [tab, setTab] = useState('clients');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/reports/${tab}?year=${year}`).then(setData);
  }, [tab, year]);
  const rows = data?.rows || [];
  const sum = key => r2(rows.reduce((s, x) => s + (Number(x[key]) || 0), 0));

  return (
    <div>
      <PageHead icon="📈" title="報表中心 Reports" desc="客戶關係報表 · 供應商報表 · 項目利潤表 — 指定年度分析" />
      <div className="row mb" style={{ justifyContent: 'space-between' }}>
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button className={`tab ${tab === 'clients' ? 'active' : ''}`} onClick={() => setTab('clients')}>🤝 客戶關係報表</button>
          <button className={`tab ${tab === 'suppliers' ? 'active' : ''}`} onClick={() => setTab('suppliers')}>🚚 供應商報表</button>
          <button className={`tab ${tab === 'projects' ? 'active' : ''}`} onClick={() => setTab('projects')}>📊 項目利潤表 P&L</button>
        </div>
        <select className="select" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
          {years.map(y => <option key={y} value={y}>{y} 年</option>)}
        </select>
      </div>

      <div className="card">
        {data === null ? <Loading /> : rows.length === 0 ? <Empty icon="📈" text={`${year} 年沒有數據`} /> : (
          <>
            {tab === 'clients' && (
              <>
                <div className="card-title">🤝 {year} 年客戶關係報表 (全部 {rows.length} 個活躍客戶)</div>
                {rows.length > 1 ? (
                  <div className="mb"><div className="muted mb" style={{ padding: '0 4px' }}>總接單額 Top 對比</div>
                    <BarChart data={rows.slice(0, 6).map(x => ({ l: x.name.slice(0, 6), v: x.order_total }))} />
                  </div>
                ) : null}
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead><tr><th>#</th><th>客戶</th><th className="num">訂單數</th><th className="num">總接單額</th><th className="num">已開票</th><th className="num">已收金額</th><th className="num">未收尾數</th></tr></thead>
                    <tbody>
                      {rows.map((c, i) => (
                        <tr key={c.id} className="clickable" onClick={() => nav('/customers')}>
                          <td className="muted">{i + 1}</td>
                          <td><div className="strong">{c.name}</div><div className="sub">{c.contact} · {c.phone}</div></td>
                          <td className="num">{c.order_count}</td>
                          <td className="num strong">{money(c.order_total)}</td>
                          <td className="num">{money(c.invoiced)}</td>
                          <td className="num" style={{ color: 'var(--mint)' }}>{money(c.received)}</td>
                          <td className="num strong">{money(c.outstanding)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: 'var(--surface-soft)', fontWeight: 800 }}>
                        <td></td><td>總計 TOTAL</td>
                        <td className="num">{rows.reduce((s, x) => s + x.order_count, 0)}</td>
                        <td className="num">{money(sum('order_total'))}</td>
                        <td className="num">{money(sum('invoiced'))}</td>
                        <td className="num">{money(sum('received'))}</td>
                        <td className="num">{money(sum('outstanding'))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === 'suppliers' && (
              <>
                <div className="card-title">🚚 {year} 年供應商報表</div>
                {rows.length > 1 ? (
                  <div className="mb"><div className="muted mb" style={{ padding: '0 4px' }}>採購總額對比</div>
                    <BarChart data={rows.slice(0, 6).map(x => ({ l: x.name.slice(0, 6), v: x.po_total }))} color="#aaaaaa" />
                  </div>
                ) : null}
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead><tr><th>#</th><th>供應商</th><th className="num">PO 數</th><th className="num">採購總額</th><th className="num">供應商發票</th><th className="num">已付金額</th><th className="num">未付帳款</th></tr></thead>
                    <tbody>
                      {rows.map((s, i) => (
                        <tr key={s.id}>
                          <td className="muted">{i + 1}</td>
                          <td><div className="strong">{s.name}</div><div className="sub">{s.contact} · {s.phone}</div></td>
                          <td className="num">{s.po_count}</td>
                          <td className="num strong">{money(s.po_total)}</td>
                          <td className="num">{money(s.si_total)}</td>
                          <td className="num" style={{ color: 'var(--mint)' }}>{money(s.paid)}</td>
                          <td className="num strong" style={{ color: s.outstanding > 0 ? 'var(--amber)' : 'var(--mint)' }}>{money(s.outstanding)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: 'var(--surface-soft)', fontWeight: 800 }}>
                        <td></td><td>總計 TOTAL</td>
                        <td className="num">{rows.reduce((s, x) => s + x.po_count, 0)}</td>
                        <td className="num">{money(sum('po_total'))}</td>
                        <td className="num">{money(sum('si_total'))}</td>
                        <td className="num">{money(sum('paid'))}</td>
                        <td className="num">{money(sum('outstanding'))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === 'projects' && (
              <>
                <div className="card-title">📊 {year} 年項目利潤表 (開票收入 − 分判成本)</div>
                {rows.length > 1 ? (
                  <div className="mb"><div className="muted mb" style={{ padding: '0 4px' }}>每張工程利潤</div>
                    <BarChart data={rows.slice(0, 8).map(x => ({ l: x.order_no.slice(-4), v: x.profit }))} color="#dedede" />
                  </div>
                ) : null}
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead><tr><th>訂單</th><th>工程 / 客戶</th><th>日期</th><th className="num">合約額</th><th className="num">開票</th><th className="num">已收</th><th className="num">PO 成本</th><th className="num">利潤</th><th className="num">毛利%</th></tr></thead>
                    <tbody>
                      {rows.map(o => (
                        <tr key={o.id} className="clickable" onClick={() => nav(`/orders/${o.id}`)}>
                          <td className="strong">{o.order_no}</td>
                          <td><div>{o.title || '—'}</div><div className="sub">{o.customer_name}</div></td>
                          <td>{fmtDate(o.order_date)}</td>
                          <td className="num">{money(o.order_total)}</td>
                          <td className="num">{money(o.invoiced)}</td>
                          <td className="num">{money(o.received)}</td>
                          <td className="num">{money(o.po_total)}</td>
                          <td className="num strong" style={{ color: o.profit >= 0 ? 'var(--mint)' : 'var(--coral)' }}>{o.profit >= 0 ? '+' : ''}{money(o.profit)}</td>
                          <td className="num"><span className={`st ${o.margin >= 0 ? 'st-green' : 'st-red'}`}>{o.margin}%</span></td>
                        </tr>
                      ))}
                      <tr style={{ background: 'var(--surface-soft)', fontWeight: 800 }}>
                        <td>總計</td><td>{rows.length} 項工程</td><td></td>
                        <td className="num">{money(sum('order_total'))}</td>
                        <td className="num">{money(sum('invoiced'))}</td>
                        <td className="num">{money(sum('received'))}</td>
                        <td className="num">{money(sum('po_total'))}</td>
                        <td className="num" style={{ color: sum('profit') >= 0 ? 'var(--mint)' : 'var(--coral)' }}>{money(sum('profit'))}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
